import React, { useMemo, useCallback } from "react";
import { Cascader } from "@certa/catalyst/components/Cascader";
import { getLayoutContainer, Stack, Text } from "@certa/blocks/thanos";
import { useIntl } from "react-intl";
import { css } from "emotion";
import Highlighter from "react-highlight-words";
import { ArrowRightLight } from "@certa/icons/components/ArrowRightLight";
import { removeHTMLTagsAndTruncate } from "../../utils/sanitizeHTML";
import { getLastArrayElement } from "../../utils/helper";

import type { ObjectField, ObjectFieldCascaderOption } from "./types";
import {
  FIELD_CATEGORY_LABELS,
  OBJECT_FIELD_TYPES,
  SMART_COLUMN_FIELDS
} from "./constants";
import type { VFC } from "react";
import type { IconProps } from "@certa/icons";

import { Characters } from "@certa/icons/components/Characters";
import { Number } from "@certa/icons/components/Number";
import { Boolean } from "@certa/icons/components/Boolean";
import { Date } from "@certa/icons/components/Date";
import { Envelope } from "@certa/icons/components/Envelope";
import { Link } from "@certa/icons/components/Link";
import { Phone } from "@certa/icons/components/Phone";
import { Globe } from "@certa/icons/components/Globe";
import { UserCircle } from "@certa/icons/components/UserCircle";
import { Array } from "@certa/icons/components/Array";
import { Kanban } from "@certa/icons/components/Kanban";
import { SingleFile } from "@certa/icons/components/SingleFile";
import { Badge } from "@certa/icons/components/Badge";
import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";

const SCREEN_HEIGHT_THRESHOLD = 900;

const OBJECT_FIELD_TYPE_ICON_MAP: Record<string, VFC<IconProps>> = {
  [OBJECT_FIELD_TYPES.string]: Characters,
  [OBJECT_FIELD_TYPES.email]: Envelope,
  [OBJECT_FIELD_TYPES.url]: Link,
  [OBJECT_FIELD_TYPES.phoneNumber]: Phone,
  [OBJECT_FIELD_TYPES.country]: Globe,
  [OBJECT_FIELD_TYPES.owner]: UserCircle,
  [OBJECT_FIELD_TYPES.number]: Number,
  [OBJECT_FIELD_TYPES.integer]: Number,
  [OBJECT_FIELD_TYPES.boolean]: Boolean,
  [OBJECT_FIELD_TYPES.date]: Date,
  [OBJECT_FIELD_TYPES.dateTime]: Date,
  [OBJECT_FIELD_TYPES.array]: Array,
  [OBJECT_FIELD_TYPES.badgeTags]: Badge,
  category: Kanban,
  default: SingleFile
};

const objectFieldLabelWrapper = (
  label: React.ReactNode,
  type: string,
  labelColor: any = "inherit"
): React.ReactNode => {
  const IconComponent =
    OBJECT_FIELD_TYPE_ICON_MAP[type] || OBJECT_FIELD_TYPE_ICON_MAP.default;
  const isLabelString = typeof label === "string";
  const displayLabel = isLabelString ? removeHTMLTagsAndTruncate(label) : label;

  return (
    <Stack gap="s2" inline align="center" itemWidth="100%">
      <IconComponent size={16} color={labelColor} style={{ flexShrink: 0 }} />
      <Text
        variant="p1-regular"
        color={labelColor}
        title={isLabelString ? (displayLabel as string) : undefined}
        className={css`
          width: 100% !important;
        `}
      >
        {isLabelString ? (
          <span
            dangerouslySetInnerHTML={{
              __html: displayLabel as string
            }}
          />
        ) : (
          label
        )}
      </Text>
    </Stack>
  );
};

// Transform ObjectField[] into cascader options structure
const ObjectFieldTreeWrapper = (
  fieldOptions: ObjectField[],
  hideSmartColumns: boolean
): ObjectFieldCascaderOption[] => {
  const cascaderOptions: ObjectFieldCascaderOption[] = [];

  // Add Fields section from fieldOptions
  if (fieldOptions.length > 0) {
    cascaderOptions.push({
      value: "fields",
      label: objectFieldLabelWrapper(FIELD_CATEGORY_LABELS.FIELDS, "category"),
      labelText: FIELD_CATEGORY_LABELS.FIELDS,
      children: fieldOptions.map(field => ({
        value: field.tag,
        label: objectFieldLabelWrapper(field.label, field.type),
        labelText: field.label,
        fieldData: field
      }))
    });
  }

  // Add Attributes section from SMART_COLUMN_FIELDS
  if (!hideSmartColumns)
    cascaderOptions.push({
      value: "attributes",
      label: objectFieldLabelWrapper(
        FIELD_CATEGORY_LABELS.ATTRIBUTES,
        "category"
      ),
      labelText: FIELD_CATEGORY_LABELS.ATTRIBUTES,
      children: SMART_COLUMN_FIELDS.map(field => ({
        value: field.tag,
        label: objectFieldLabelWrapper(field.label, field.type),
        labelText: field.label,
        fieldData: {
          tag: field.tag,
          label: field.label,
          type: field.type,
          category: "attribute"
        } as ObjectField
      }))
    });

  return cascaderOptions;
};

// Simple cascader search configuration
const cascaderSearchConfig = {
  filter: (inputValue: string, path: ObjectFieldCascaderOption[]) => {
    const searchValue = inputValue.toLowerCase();
    return path.some(
      option =>
        option.labelText?.toLowerCase().includes(searchValue) ||
        option.value?.toString().toLowerCase().includes(searchValue)
    );
  },
  render: (inputValue: string, path: ObjectFieldCascaderOption[]) => {
    return path.map((option: ObjectFieldCascaderOption, index: number) => {
      const label = option?.labelText || "";
      const isMatch = label.toLowerCase().includes(inputValue.toLowerCase());

      const node = isMatch ? (
        <Highlighter
          highlightClassName={css`
            color: #ff4d4f;
            background-color: white;
            padding: 0;
          `}
          searchWords={[inputValue]}
          autoEscape={true}
          textToHighlight={label}
        />
      ) : (
        label
      );

      const labelColor = index > 0 ? "inherit" : "neutral-70";

      return [
        <ArrowRightLight
          key="arrow-icon"
          color={"neutral-70"}
          className={css`
            margin: 0 var(--s2);
            display: ${index === 0 && "none"};
          `}
        />,
        <Stack
          key="stack"
          inline
          align={"center"}
          gap="s1"
          className={css`
            max-width: 180px;
          `}
        >
          {objectFieldLabelWrapper(
            node,
            option.fieldData?.type || (index === 0 ? "category" : "default"),
            labelColor
          )}
        </Stack>
      ];
    });
  },
  sort: (a: ObjectFieldCascaderOption[], b: ObjectFieldCascaderOption[]) => {
    const aText = a[a.length - 1]?.labelText || "";
    const bText = b[b.length - 1]?.labelText || "";
    return aText.localeCompare(bText);
  },
  limit: 50,
  matchInputWidth: false
};

type Props = {
  fieldOptions: ObjectField[];
  selectedField?: ObjectField | null;
  onFieldSelect: (field: ObjectField | null) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  loading?: boolean;
  hideSmartColumns: boolean;
};

export const ObjectFieldDropdown: React.FC<Props> = ({
  fieldOptions,
  selectedField,
  onFieldSelect,
  disabled = false,
  placeholder,
  className,
  loading = false,
  hideSmartColumns
}) => {
  const intl = useIntl();
  const cascaderOptions = useMemo(
    () => ObjectFieldTreeWrapper(fieldOptions, hideSmartColumns),
    [fieldOptions, hideSmartColumns]
  );

  const label =
    placeholder ||
    intl.formatMessage({
      id: "common.selectField",
      defaultMessage: "Select a field"
    });

  const cascaderPlacement =
    window.innerHeight > SCREEN_HEIGHT_THRESHOLD ? "bottomLeft" : "topLeft";

  // Get the cascader value from selected field
  const cascaderValue = useMemo(() => {
    if (!selectedField) return undefined;

    // Check if the field is from SMART_COLUMN_FIELDS (attributes)
    const isSmartColumnField = SMART_COLUMN_FIELDS.some(
      field => field.tag === selectedField.tag
    );

    const category = isSmartColumnField ? "attributes" : "fields";
    return [category, selectedField.tag];
  }, [selectedField]);

  const handleCascaderChange = useCallback(
    (
      value: string[] | undefined,
      selectedOptions?: ObjectFieldCascaderOption[]
    ) => {
      if (!value || value.length === 0) {
        onFieldSelect(null);
        return;
      }

      if (value.length < 2) {
        return;
      }

      const [category, fieldTag] = value;
      let field: ObjectField | null = null;

      if (category === "fields") {
        // Look for field in fieldOptions
        field = fieldOptions.find(f => f.tag === fieldTag) || null;
      } else if (category === "attributes") {
        // Look for field in SMART_COLUMN_FIELDS
        const smartField = SMART_COLUMN_FIELDS.find(f => f.tag === fieldTag);
        if (smartField) {
          field = {
            tag: smartField.tag,
            label: smartField.label,
            type: smartField.type,
            category: "attribute"
          } as ObjectField;
        }
      }

      if (field) {
        onFieldSelect(field);
      }
    },
    [fieldOptions, onFieldSelect]
  );

  const NO_FIELDS_AVAILABLE = intl.formatMessage({
    id: "common.noFieldsAvailable",
    defaultMessage: "No fields available"
  });

  return (
    <div className={className}>
      <Cascader
        minWidth="200px"
        placement={cascaderPlacement}
        options={cascaderOptions}
        showSearch={cascaderSearchConfig}
        onChange={handleCascaderChange}
        value={cascaderValue}
        disabled={disabled}
        label={label}
        placeholder={label}
        loading={loading}
        allowClear
        notFoundContent={
          <Typography
            variant={TypographyVariants.BODY}
            color={TypographyColors.NEUTRAL_700}
          >
            {NO_FIELDS_AVAILABLE}
          </Typography>
        }
        displayRender={(
          _: string[],
          selectedOptions?: ObjectFieldCascaderOption[]
        ) => {
          const title = getLastArrayElement(selectedOptions)?.labelText;
          return <div title={title}>{title}</div>;
        }}
        getPopupContainer={() => getLayoutContainer()}
      />
    </div>
  );
};
